import { cloneDeep, omit, update, get, isEmpty } from 'lodash';
import { v1 as uuidv1 } from 'uuid';

import {
  // QUESTION TYPES
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_DESCRIPTION,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_NPS,
  QUESTION_VAN_WESTENDORP,
  QUESTION_MAXDIFF,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,

  // GROUP TYPES
  GROUP_BASIC,
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
  QUESTION_CONVERSATIONAL,
  GROUP_LOOP_MERGE,
} from 'data/nodeTypes';
import { getQuestionWithCleanAnswers } from 'utils/questionHelpers';
import { CONJOINT_ATTRIBUTE_TYPES } from 'containers/QuestionnairePage/QuestionGroup/Conjoint/constants';
import { getTicks } from 'containers/QuestionnairePage/GenericQuestion/SliderQuestion/utils';

function renameKey(obj, oldKey, newKey) {
  if (oldKey in obj) {
    const { [oldKey]: value, ...restOfObject } = obj;
    return { [newKey]: value, ...restOfObject };
  }
  return obj;
}

function transformUpsertToFetch(upsertData) {
  const clonedData = cloneDeep(upsertData);
  const {
    logic,
    disqualificationRules,
    questions,
    questionGroups,
    ...restOfData
  } = clonedData;
  const transformed = restOfData;

  if (disqualificationRules) {
    Object.assign(transformed, {
      disqualificationRules: transformDisqualificationRules(
        disqualificationRules,
      ),
    });
  }
  if (questions) {
    Object.assign(transformed, {
      questions: questions.map(transformQuestion),
    });
  }

  if (questionGroups) {
    Object.assign(transformed, {
      questionGroups: questionGroups.map(transformQuestionGroup),
    });
  }

  return transformed;
}

function transformQuestionGroup(questionGroup) {
  switch (questionGroup.groupType) {
    case GROUP_CONJOINT:
      return transformConjointQuestionGroup(questionGroup);
    case GROUP_VAN_WESTENDORP:
      return transformVanWestendorpQuestionGroup(questionGroup);
    case GROUP_MAXDIFF:
      return transformMaxDiffQuestionGroup(questionGroup);
    case GROUP_LOOP_MERGE:
    case GROUP_AB_TEST:
    case GROUP_BASIC:
      return questionGroup;
    default:
      return questionGroup;
  }
}

function ifMissingThen(defaultValue) {
  return function getDefaultValue(currentValue) {
    if (currentValue === undefined) {
      return defaultValue;
    }
    return currentValue;
  };
}

function transformMaxDiffQuestionGroup(questionGroup) {
  update(
    questionGroup,
    'attributeSet.properties.excludedCombinations',
    ifMissingThen([]),
  );
  update(
    questionGroup,
    'attributeSet.properties.doeStructureVersion',
    ifMissingThen(0),
  );
  update(
    questionGroup,
    'attributeSet.properties.conceptsLabel',
    ifMissingThen(''),
  );
  update(
    questionGroup,
    'attributeSet.attributes[0].type',
    ifMissingThen(CONJOINT_ATTRIBUTE_TYPES.FEATURE),
  );

  return questionGroup;
}

function transformConjointQuestionGroup(questionGroup) {
  update(
    questionGroup,
    'attributeSet.properties.excludedCombinations',
    ifMissingThen([]),
  );
  update(
    questionGroup,
    'attributeSet.properties.doeStructureVersion',
    ifMissingThen(0),
  );

  return questionGroup;
}

function transformVanWestendorpQuestionGroup(questionGroup) {
  return questionGroup;
}

function transformQuestion(originalQuestion) {
  // add hasPiping for answers if it's missing
  const question = getQuestionWithCleanAnswers(originalQuestion);

  switch (question.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return transformMultipleSelectionQuestion(question);
    case QUESTION_SLIDER:
      return transformSliderQuestion(question);
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
      return transformOpenEndedQuestion(question);
    case QUESTION_DESCRIPTION:
      return transformDescriptionQuestion(question);
    case QUESTION_RATING:
      return transformRatingStarsQuestion(question);
    case QUESTION_NPS:
      return transformNPSQuestion(question);
    case QUESTION_MATRIX_BIPOLAR:
      return transformBipolarQuestion(question);
    case QUESTION_MATRIX_OPEN_ENDED:
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
      return transformNumericOpenEndedMatrixQuestion(question);
    case QUESTION_VAN_WESTENDORP:
      return transformVanWestendorpQuestion(question);
    case QUESTION_RANKING:
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return transformDefaultQuestion(question);
    case QUESTION_MAXDIFF:
      return transformMaxDiffQuestion(question);
    case QUESTION_MATRIX_CONSTANT_SUM:
      return transformConstantSumQuestion(question);
    case QUESTION_DRILLDOWN:
      return transformDrillDownQuestion(question);
    case QUESTION_CONVERSATIONAL:
      return transformConversationalQuestion(question);
    default:
      return transformDefaultQuestion(question);
  }
}

function transformQuestionCommonProperties(question) {
  update(
    question,
    'htmlPrompt',
    ifMissingThen(`<div>${question.prompt}</div>`),
  );

  const {
    hasScreeningQuotas = false,
    matrix = {},
    answers = [],
    scorepoints = [],
    comments = [],
    type,
    shuffle = 0,
    slider,
    relatedQuestions,
    selectedAttributes,
  } = question;

  return {
    ...question,
    hasScreeningQuotas,
    matrix,
    answers,
    scorepoints,
    comments,
    style: type,
    shuffle,
    slider,
    relatedQuestions,
    selectedAttributes,
  };
}

function transformSliderQuestion(question) {
  const { slider: slideQuestionDto } = question;
  const transformedSlideDto = renameKey(
    slideQuestionDto,
    'id',
    'slideQuestionId',
  );

  const ticks = get(transformedSlideDto, 'ticks', []);
  if (isEmpty(ticks)) {
    update(transformedSlideDto, 'ticks', ifMissingThen([]));
  } else {
    const { minValue, maxValue } = slideQuestionDto;
    const updatedTicks = getTicks({
      ticks: ticks.concat([{}]), // add one empty tick, because the ticks expect it
      minValue,
      maxValue,
    });

    ticks.forEach((tick, index) => {
      update(
        transformedSlideDto,
        `ticks[${index}].tickValue`,
        ifMissingThen(updatedTicks[index].tickValue),
      );
    });
  }

  return {
    ...omit(transformQuestionCommonProperties(question), [
      'matrix',
      'slider',
      'answers',
    ]),
    slideQuestionDto: transformedSlideDto,
  };
}

function transformNPSQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), [
      'matrix',
      'slider',
      'answers',
    ]),
  };
}

function transformDrillDownQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}

export function transformConversationalQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
    answers: question.answers?.map(omitIsExclusiveAnswer),
  };
}

function transformMultipleSelectionQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['matrix', 'slider']),
    answers: question.answers?.map(addIsExclusiveAnswer),
  };
}
function transformOpenEndedQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['matrix', 'slider']),
    answers: [],
  };
}
function transformRatingStarsQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['matrix', 'slider']),
    answers: [],
  };
}
function transformDefaultQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['matrix', 'slider']),
    answers: question.answers?.map(omitIsExclusiveAnswer),
  };
}
function transformBipolarQuestion(question) {
  update(
    question,
    'matrix.openEndedConfig',
    ifMissingThen({ style: 'HORIZONTAL', refId: uuidv1() }),
  );

  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}
function transformNumericOpenEndedMatrixQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}
function transformDescriptionQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), [
      'slider',
      'matrix',
      'answers',
    ]),
  };
}
function transformVanWestendorpQuestion(question) {
  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}
function transformConstantSumQuestion(question) {
  update(question, 'matrix.openEndedConfig.sign', ifMissingThen(null));
  update(
    question,
    'matrix.validation.openEndedValidation.decimals',
    ifMissingThen(null),
  );
  update(
    question,
    'matrix.validation.openEndedValidation.max',
    ifMissingThen(null),
  );
  update(
    question,
    'matrix.validation.openEndedValidation.min',
    ifMissingThen(null),
  );

  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}

function transformMaxDiffQuestion(question) {
  update(question, 'matrix.answerColumnValueTypes', ifMissingThen(null));
  update(question, 'matrix.capabilities', ifMissingThen(null));
  update(question, 'matrix.matrixAnswers', ifMissingThen(null));
  update(question, 'matrix.validation', ifMissingThen({}));

  const columns = get(question, 'matrix.columns', []);
  columns.forEach((column, index) => {
    update(column, 'sort', ifMissingThen(index));
  });

  return {
    ...omit(transformQuestionCommonProperties(question), ['slider']),
  };
}

function addIsExclusiveAnswer(answer = {}) {
  const { isExclusive = false, ...restOfAnswer } = answer;
  return { ...restOfAnswer, isExclusive };
}
function omitIsExclusiveAnswer(answer = {}) {
  return omit(answer, ['isExclusive']);
}

function transformDisqualificationRules(rules = {}) {
  const transformed = cloneDeep(rules);
  const {
    disqualifiedUsers = 0,
    status = 0,
    ...restOfDisqualificationRules
  } = transformed;

  return {
    ...restOfDisqualificationRules,
    disqualifiedUsers,
    status,
  };
}

export { transformUpsertToFetch };
